"use strict";

import * as olProj from 'ol/proj';
import * as olCoordinate from 'ol/coordinate';
import MousePosition from "ol/control/MousePosition";
import Draw from "ol/interaction/Draw";
import jQuery from 'jquery';

/**
 * Control zur Anzeige/zum Kopieren der aktuell unter der Maus befindlichen Koordinaten.
 *
 * @class
 * @extends gc.gis.control.GCAbstractMapControl
 * @memberof gc.gis.control
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen (siehe {@link gc.gis.control.GCAbstractMapControl})
 */
gc.gis.control.OLMousePosition = function( options )
{
	gc.gis.control.GCAbstractMapControl.call( this, options );

	/**
	 * @private
	 * @type {ol.control.MousePosition}
	 */
	this.__control = undefined;

	/**
	 * Bedienungshinweis, wenn Kopiermodus aktiv ist
	 * @private
	 * @type {gc.gis.ui.GCInteractionMessage}
	 */
	this.__interaction_message;
	if( gc.gis.iface.env.device === gc.gis.env.device.MOBILE )
	{
		this.__interaction_message = {
			title:   'Koordinate abrufen',
			icon:    'touch-pointer',
			message: 'Tippen Sie auf die Karte.'
		};
	}
	else
	{
		this.__interaction_message = {
			title:   'Koordinate abrufen',
			icon:    'mouse-button-left',
			message: 'Klicken Sie mit der linken Maustaste auf die Karte.'
		};
	}

	/**
	 * @private
	 * @type {ol.source.Vector}
	 */
	this.__source = undefined;

	/**
	 * @private
	 * @type {ol.layer.Vector}
	 */
	this.__layer = undefined;

	/**
	 * @private
	 * @type {ol.interaction.Draw}
	 */
	this.__interaction = undefined;

	/**
	 * @private
	 * @type {ol.style.Style}
	 */
	this.__style_done = undefined;	// FIXME: Style existiert noch nicht -> default
};
gc.gis.control.OLMousePosition.prototype = Object.create( gc.gis.control.GCAbstractMapControl.prototype );

/**
 * @inheritdoc
 */
gc.gis.control.OLMousePosition.prototype.init = function()
{
	const self = this;
	const ol_map = this._map.getMap().getImpl();

	// Control hinzufügen
	this.__control = new MousePosition({
		target:        self._target.replace('#', ''),
		className:     'ol-mouse-position' + (self._className ? ' '+ self._className : ''),
		undefinedHTML: '<span class="key-value">'
		              +'<span class="key">X:</span> '
					  +'<span class="value">-</span>'
					  +'</span> '
					  +'<span class="key-value">'
					  +'<span class="key">Y:</span> '
					  +'<span class="value">-</span>'
					  +'</span>',
		coordinateFormat: function( c ) {
			const ol_proj = olProj.get(self._map.getProjection());
			let decimals = 5;
			if (ol_proj.getUnits() !== 'degrees')
			{
				decimals = 3;
			}
			const html = '<span class="key-value">'
				+'<span class="key">X:</span> '
				+'<span class="value">{x}</span>'
				+'</span> '
				+'<span class="key-value">'
				+'<span class="key">Y:</span> '
				+'<span class="value">{y}</span>'
				+'</span>';
			return olCoordinate.format(c, html, decimals);
		}
	});

	ol_map.addControl( this.__control );

	gc.gis.iface.ui.tooltip(jQuery(this.__control.element), {
		placement: 'auto top',
		title: 'Klicken um Koordinate in Zwischenablage zu kopieren'
	});

	jQuery( this.__control.element ).on('click', function( evt ) {
		self.toggleInteraction();
	});

	// Interaktion hinzufügen
	this.__layer = new gc.gis.layer.OLTrackingLayer(this._map.getMap());    // Wrapper nutzen, und dann mit OL-Instanzen weiterarbeiten
	this.__source = this.__layer.getImpl().getSource();
	this.__layer.getImpl().setStyle(this.__style_done);

	this._map.getMap().addTrackingLayer(this.__layer);

	this.__interaction = new Draw({
		type:   'Point',
		source: this.__source
	});
	this.__interaction.setActive( false );
	this.__interaction.set('_is_gc', true, true);
	this.__interaction.set('_gc_name', 'CoordinateInfo', true);
	ol_map.addInteraction( this.__interaction );

	this.__interaction.on('drawend', function() {
		try
		{
			// Interaktion deaktivieren
			self.copyToClipboard();
			self.__interaction.setActive( false );

			// Alte Ergebnisse aufräumen
			self.__source.clear();
		}
		catch( err )
		{
			gc.gis.iface.log.error( this, err );
		}
	}, this );

	this._map.on( gc.gis.map.GCMapEvent.INTERACTION_ACTIVATED, this, function(evt ) {
		if( evt.interaction === self.__interaction )
		{
			gc.gis.iface.log.debug( self, self.__interaction.get('_gc_name') + ' was activated.');
			self.onInteractionActivated();
		}
	});
	this._map.on( gc.gis.map.GCMapEvent.INTERACTION_DEACTIVATED, this, function(evt ) {
		if( evt.interaction === self.__interaction )
		{
			gc.gis.iface.log.debug( self, self.__interaction.get('_gc_name') + ' was deactivated.');
			self.onInteractionDeactivated();
		}
	});
};

/**
 * Schaltet die Kopier-Interaktion an, wenn aus bzw. vice versa.
 *
 * @public
 */
gc.gis.control.OLMousePosition.prototype.toggleInteraction = function()
{
	this.__interaction.setActive(!this.__interaction.getActive());
};

/**
 * Blendet die Interaktionshilfe ein. Callback für Openlayers-Event.
 *
 * @public
 */
gc.gis.control.OLMousePosition.prototype.onInteractionActivated = function()
{
	if (this.__control.element)
	{
		jQuery( this.__control.element ).addClass('gc-active');
		gc.gis.iface.ui.setInteractionMessage( this.__interaction_message );
	}
};

/**
 * Blendet die Interaktionshilfe aus. Callback für Openlayers-Event.
 *
 * @public
 */
gc.gis.control.OLMousePosition.prototype.onInteractionDeactivated = function()
{
	if( this.__control.element )
	{
		jQuery( this.__control.element ).removeClass('gc-active');
		gc.gis.iface.ui.unsetInteractionMessage( this.__interaction_message );
	}
};

/**
 * Kopiert die aktuelle Koordinate in die Zwischenanlage.
 *
 * @public
 * @throws gc.gis.error.GCUnsupportedActionError wenn der Browser das Kopieren
 *   nicht unterstützt
 */
gc.gis.control.OLMousePosition.prototype.copyToClipboard = function()
{
	// Kopier-Inhalt zusammenfriemeln
	const content = jQuery.map(
		jQuery( this.__control.element ).find('span.value'),
		function( element ) {
			return jQuery( element ).text();
		}
	).join(', ');

	gc.gis.GCUtil.copyToClipboard(content);
};

